/**
 * SecuriteRoutiereStats — Composant d'affichage des chiffres clés ONISR.
 *
 * Variantes :
 *  - variant="compact" : 4 chiffres en grille (homepage / hub)
 *  - variant="full" : version complète avec causes + faits jury (page CCS)
 *  - variant="jury" : focus citations jury CCS (mémorisation)
 *
 * Mise à jour des chiffres : voir /app/frontend/src/data/securiteRoutiere.js
 */
import React from 'react';
import { ExternalLink, AlertTriangle, TrendingUp, Quote } from 'lucide-react';
import {
  SECURITE_ROUTIERE_META, STATS_GLOBALES, STATS_2RM,
  STATS_CAUSES, FAITS_CCS_JURY,
} from '../data/securiteRoutiere';

const formatNumber = (n) => n.toLocaleString('fr-FR');
const formatDate = (iso) => {
  const d = new Date(iso);
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
};

function StatTile({ value, unit = '', label, subtext, color = '#cf2031', testid }) {
  return (
    <div
      className="rounded-2xl border-2 bg-card p-4 transition-all hover:shadow-md"
      style={{ borderColor: `${color}33` }}
      data-testid={testid}
    >
      <div
        className="text-3xl sm:text-4xl font-black tabular-nums leading-none"
        style={{ color, fontFamily: "'Playfair Display', Georgia, serif" }}
      >
        {typeof value === 'number' ? formatNumber(value) : value}
        {unit && <span className="text-xl ml-0.5">{unit}</span>}
      </div>
      <div className="text-[10px] font-black uppercase tracking-widest text-foreground/70 mt-2 leading-tight">
        {label}
      </div>
      {subtext && (
        <div className="text-xs text-muted-foreground mt-1.5 leading-snug">{subtext}</div>
      )}
    </div>
  );
}

/**
 * Détecte si les données stockées datent de plus de N mois.
 * Permet d'alerter automatiquement l'admin quand le nouveau Bilan ONISR
 * (publié chaque année en mars/avril) doit être intégré.
 *
 * → Pas de cron, pas d'API à maintenir : la détection se fait côté client
 * à chaque chargement, et un bandeau discret apparaît si nécessaire.
 */
function isStale(monthsThreshold = 14) {
  try {
    const last = new Date(SECURITE_ROUTIERE_META.lastUpdate);
    const now = new Date();
    const diffMs = now - last;
    const diffMonths = diffMs / (1000 * 60 * 60 * 24 * 30.44);
    return diffMonths > monthsThreshold;
  } catch {
    return false;
  }
}

function StaleAlert() {
  if (!isStale()) return null;
  return (
    <div
      className="rounded-lg border-2 border-amber-400 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 mt-3 text-xs"
      data-testid="onisr-stale-alert"
    >
      <strong className="text-amber-700 dark:text-amber-400">⚠ Données potentiellement obsolètes</strong>
      <span className="text-amber-900 dark:text-amber-200 ml-1">
        — vérifie le <a href="https://www.onisr.securite-routiere.gouv.fr/" target="_blank" rel="noopener noreferrer" className="underline font-bold">dernier Bilan ONISR</a> et mets à jour <code className="bg-amber-100 dark:bg-amber-900/40 px-1 rounded">data/securiteRoutiere.js</code>.
      </span>
    </div>
  );
}

function SourceFooter() {
  // Filtre les sources désactivées (active === false) — permet de masquer
  // les URLs cassées sans les supprimer du fichier data. Voir securiteRoutiere.js
  const activeSources = (SECURITE_ROUTIERE_META.sources || []).filter(
    (s) => s && s.url && s.active !== false
  );
  return (
    <footer className="mt-4 pt-3 border-t border-border/50 flex items-start gap-2 flex-wrap text-[11px] text-muted-foreground">
      <span className="font-bold">📊 Sources officielles ·</span>
      <span>Mis à jour le {formatDate(SECURITE_ROUTIERE_META.lastUpdate)}</span>
      <span>·</span>
      <span>Bilan {SECURITE_ROUTIERE_META.bilanYear} ({SECURITE_ROUTIERE_META.bilanStatus})</span>
      {activeSources.length > 0 && (
        <span className="basis-full mt-1 flex flex-wrap gap-x-3">
          {activeSources.map((s) => (
            <a
              key={s.url}
              href={s.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 hover:underline text-primary"
              data-testid={`onisr-source-link-${s.url}`}
            >
              {s.label} <ExternalLink className="w-3 h-3" />
            </a>
          ))}
        </span>
      )}
      <StaleAlert />
    </footer>
  );
}

export default function SecuriteRoutiereStats({ variant = 'compact', className = '' }) {
  if (variant === 'jury') {
    return (
      <section
        className={`rounded-2xl border-2 border-amber-400/40 bg-gradient-to-br from-amber-50/80 via-card to-amber-50/30 dark:from-amber-950/20 dark:to-amber-950/10 p-4 sm:p-5 ${className}`}
        data-testid="stats-jury"
      >
        <header className="flex items-start gap-2 mb-3">
          <Quote className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-[10px] font-black uppercase tracking-widest text-amber-700">
              À citer face au jury CCS
            </p>
            <h3 className="text-base sm:text-lg font-black leading-tight">
              5 chiffres clés mémorables — Bilan ONISR {SECURITE_ROUTIERE_META.bilanYear}
            </h3>
          </div>
        </header>
        <ul className="space-y-2">
          {FAITS_CCS_JURY.map((fait, i) => (
            <li key={i} className="flex items-start gap-2 text-sm leading-relaxed">
              <span className="inline-block w-5 h-5 rounded-full bg-amber-200 text-amber-900 font-black text-[11px] flex items-center justify-center shrink-0 mt-0.5">
                {i + 1}
              </span>
              <span>{fait}</span>
            </li>
          ))}
        </ul>
        <SourceFooter />
      </section>
    );
  }

  if (variant === 'full') {
    return (
      <section className={`rounded-2xl border-2 bg-card p-4 sm:p-6 ${className}`} data-testid="stats-full">
        <header className="mb-4 flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-red-100 text-red-600 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-6 h-6" />
          </div>
          <div>
            <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">
              Sécurité Routière France · Bilan {SECURITE_ROUTIERE_META.bilanYear}
            </p>
            <h2 className="text-xl sm:text-2xl font-black leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              Les chiffres qui doivent changer ta conduite
            </h2>
          </div>
        </header>

        <h3 className="text-xs font-black uppercase tracking-widest text-muted-foreground mb-2">France entière</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          <StatTile value={STATS_GLOBALES.morts.value} label={STATS_GLOBALES.morts.label} subtext={STATS_GLOBALES.morts.evolution} color="#cf2031" testid="stat-morts" />
          <StatTile value={STATS_GLOBALES.blesses.value} label={STATS_GLOBALES.blesses.label} subtext={STATS_GLOBALES.blesses.evolution} color="#dc2626" testid="stat-blesses" />
          <StatTile value={STATS_GLOBALES.blessesGraves.value} label={STATS_GLOBALES.blessesGraves.label} subtext={STATS_GLOBALES.blessesGraves.evolution} color="#7c3aed" testid="stat-graves" />
          <StatTile value={STATS_GLOBALES.accidentsCorporels.value} label={STATS_GLOBALES.accidentsCorporels.label} subtext={STATS_GLOBALES.accidentsCorporels.evolution} color="#0891b2" testid="stat-accidents" />
        </div>

        <h3 className="text-xs font-black uppercase tracking-widest text-[#cf2031] mb-2">Focus 2 roues motorisées</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          <StatTile value={STATS_2RM.morts2rm.value} label={STATS_2RM.morts2rm.label} subtext={STATS_2RM.morts2rm.subtext} color="#cf2031" testid="stat-morts-2rm" />
          <StatTile value={STATS_2RM.partTrafic.value} unit="%" label={STATS_2RM.partTrafic.label} color="#0891b2" testid="stat-part-trafic" />
          <StatTile value={`×${STATS_2RM.surrisque.value}`} label={STATS_2RM.surrisque.label} color="#ea580c" testid="stat-surrisque" />
          <StatTile value={STATS_2RM.facteurComportemental.value} unit="%" label={STATS_2RM.facteurComportemental.label} color="#7c3aed" testid="stat-comportement" />
        </div>

        <h3 className="text-xs font-black uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1.5">
          <TrendingUp className="w-3.5 h-3.5" /> Causes principales des accidents mortels
        </h3>
        <div className="space-y-1.5">
          {STATS_CAUSES.map((c) => (
            <div key={c.cause} className="flex items-center gap-2 text-sm" data-testid={`cause-${c.cause.toLowerCase().split(' ')[0]}`}>
              <div className="flex-1 h-7 rounded-md bg-muted/30 overflow-hidden relative">
                <div className="h-full transition-all" style={{ width: `${c.pct * 2.5}%`, background: c.color, opacity: 0.85 }} />
                <span className="absolute inset-0 px-3 flex items-center text-xs font-bold">
                  {c.cause}
                </span>
              </div>
              <span className="text-sm font-black tabular-nums w-12 text-right" style={{ color: c.color }}>
                {c.pct} %
              </span>
            </div>
          ))}
        </div>

        <SourceFooter />
      </section>
    );
  }

  // variant === 'compact' (par défaut)
  return (
    <section
      className={`rounded-2xl border-2 border-border bg-card p-4 sm:p-5 ${className}`}
      data-testid="stats-compact"
    >
      <header className="mb-3 flex items-start gap-2">
        <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
        <div className="min-w-0">
          <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">
            Bilan ONISR {SECURITE_ROUTIERE_META.bilanYear} ({SECURITE_ROUTIERE_META.bilanStatus})
          </p>
          <h3 className="text-base sm:text-lg font-black leading-tight">
            Sécurité routière · Chiffres clés France
          </h3>
        </div>
      </header>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatTile value={STATS_GLOBALES.morts.value} label="Morts en 2024" color="#cf2031" testid="stat-c-morts" />
        <StatTile value={STATS_2RM.morts2rm.value} label="dont motards/scootéristes" color="#dc2626" testid="stat-c-2rm" />
        <StatTile value={`×${STATS_2RM.surrisque.value}`} label="Surrisque 2RM vs voiture" color="#ea580c" testid="stat-c-surrisque" />
        <StatTile value={STATS_2RM.facteurComportemental.value} unit="%" label="Accidents 2RM = facteur comportemental" color="#7c3aed" testid="stat-c-comp" />
      </div>
      <SourceFooter />
    </section>
  );
}

export { SecuriteRoutiereStats };
